export const GAME_WIDTH = 1280;
export const GAME_HEIGHT = 720;

/** Largest 1×/2×/… multiple of 1280×720 that fits, else an integer 16:9 box. */
export function integerGameViewSize(
  viewWidth: number,
  viewHeight: number,
): { width: number; height: number } {
  const integerScale = Math.floor(Math.min(viewWidth / GAME_WIDTH, viewHeight / GAME_HEIGHT));
  if (integerScale >= 1) {
    return { width: GAME_WIDTH * integerScale, height: GAME_HEIGHT * integerScale };
  }
  const n = Math.max(1, Math.floor(Math.min(viewWidth / 16, viewHeight / 9)));
  return { width: 16 * n, height: 9 * n };
}

export const RUN_DURATION_MS = 60_000;
export const STARTING_LIVES = 3;
export const INVULNERABILITY_MS = 1_500;
export const JUMP_BUFFER_MS = 120;
export const DUCK_BUFFER_MS = 120;
export const MAX_FRAME_DELTA_MS = 50;
export const FINALE_BAND_START_MS = 55_000;
export const SPAWN_CUTOFF_MS = 57_200;

export const LANE_COUNT = 3;
export const LANES = [-1, 0, 1] as const;
export type Lane = (typeof LANES)[number];

export const LANE_CHANGE_MS = 170;
export const JUMP_MS = 560;
/** Fraction of JUMP_MS that clears ground hazards. Takeoff and landing still hit. */
export const JUMP_CLEAR_START = 0.22;
export const JUMP_CLEAR_END = 0.78;
/** Descent clock multiplier after tapping Down/S in the air. */
export const FAST_FALL_MUL = 2.75;
/** Snap down to the prone duck pose. */
export const DUCK_DROP_MS = 90;
/** Brief prone beat in the middle of a tap duck. */
export const DUCK_HOLD_MS = 120;
/** Stand-up after the prone beat. */
export const DUCK_RISE_MS = 110;
/** Fraction of the rise that is still low enough to clear a branch. */
export const DUCK_LOW_END = 0.4;
/** Lift hanging branches above ground pose so there is a readable duck gap. */
export const BRANCH_LIFT = 210;
/** Draw the branch in front of the bear when it is this close, so it passes overhead. */
export const BRANCH_OVERHEAD_DISTANCE = 0.18;
export const HURT_STUN_MS = 380;
export const FINISH_SEQUENCE_MS = 2400;
/** Hold the hit clip on the last life before the game-over pose. */
export const GAME_OVER_HIT_MS = 1100;
/** Hold the game-over pose before the Game Over scene. Skip with click/key. */
export const GAME_OVER_SEQUENCE_MS = 1800;

export const COLLISION_WINDOW_MS = 140;
export const LEAD_SMOOTH_MS = 400;
export const APPROACH_LEAD_MS = {
  tutorial: 3200,
  normal: 2600,
  intense: 2100,
  finale: 2400,
} as const;

export const PATTERN_SPACING_MS = {
  tutorial: 2400,
  normal: 1900,
  intense: 1500,
  finale: 2800,
} as const;

export const LEADERBOARD_SIZE = 10;
export const DEFAULT_INITIALS = "AAA";

/**
 * Server-side score cap derived from authored encounter food values plus jump-clears.
 * A greedy 60s schedule of the richest legal patterns stays well under 10,000.
 * 15,000 leaves generous headroom without accepting impossible client scores.
 */
export const THEORETICAL_MAX_SCORE = 15_000;

export const STORAGE_KEYS = {
  audio: "hibernation-run-audio",
  howto: "hibernation-run-howto-seen",
  localScores: "hibernation-run-local-scores",
} as const;

export const BLOB_STORE_NAME = "hibernation-run-scores";

export type RunBand = keyof typeof APPROACH_LEAD_MS;

export function bandForElapsed(elapsedMs: number): RunBand {
  if (elapsedMs < 15_000) {
    return "tutorial";
  }
  if (elapsedMs < 40_000) {
    return "normal";
  }
  if (elapsedMs < FINALE_BAND_START_MS) {
    return "intense";
  }
  return "finale";
}

const BAND_START_MS: Record<RunBand, number> = {
  tutorial: 0,
  normal: 15_000,
  intense: 40_000,
  finale: FINALE_BAND_START_MS,
};

const PREVIOUS_BAND: Record<RunBand, RunBand | null> = {
  tutorial: null,
  normal: "tutorial",
  intense: "normal",
  finale: "intense",
};

/** Band target lead, interpolated over LEAD_SMOOTH_MS so the trail does not snap. */
export function smoothedApproachLeadMs(elapsedMs: number, smoothMs = LEAD_SMOOTH_MS): number {
  const band = bandForElapsed(elapsedMs);
  const target = APPROACH_LEAD_MS[band];
  const previous = PREVIOUS_BAND[band];
  if (!previous) {
    return target;
  }
  const t = Math.min(1, Math.max(0, (elapsedMs - BAND_START_MS[band]) / Math.max(1, smoothMs)));
  const from = APPROACH_LEAD_MS[previous];
  return from + (target - from) * t;
}

export function formatClock(remainingMs: number): string {
  const clamped = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}
